import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Search, Phone, Pencil, Link2, Trash2, AlertCircle } from "lucide-react";
import { AdminShell } from "@/components/admin/AdminShell";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { LogContactDialog, BUCKET_BADGE, BUCKET_LABEL } from "@/pages/admin/LogContactDialog";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { hasAdminSession } from "@/lib/session";

const STAGES = ["New", "Contacted", "Interested", "Negotiating", "Closed Won", "Closed Lost"];
const STAGE_BADGE = { "New": "bg-[#EAF1F7] text-[#1E3E5C] border border-[#C7D7E6]", "Negotiating": "bg-[#FBF1DF] text-[#7A4B12] border border-[#E7D0A8]", "Closed Won": "bg-[#E7F3EA] text-[#1F5B2E] border border-[#BFDCC7]", "Closed Lost": "bg-[#FBE7E7] text-[#7A1F1F] border border-[#E7B9B9]" };

export default function Leads() {
  const navigate = useNavigate();
  const [leads, setLeads] = useState([]);
  const [salesmen, setSalesmen] = useState([]);
  const [stage, setStage] = useState("all");
  const [q, setQ] = useState("");
  const [createOpen, setCreateOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const emptyForm = { company: "", contact_person: "", whatsapp: "", email: "", city: "", country: "", category: "", source: "", salesman_id: "", stage: "New", frequency_days: 7 };
  const [form, setForm] = useState(emptyForm);
  const [logEntity, setLogEntity] = useState(null);
  const [logOpen, setLogOpen] = useState(false);
  const [matchPrompt, setMatchPrompt] = useState(null); // { lead, shop }
  const [staff, setStaff] = useState(null);
  const [deleteFor, setDeleteFor] = useState(null);

  const load = useCallback(async () => {
    try { const { data } = await api.get("/crm/leads", { params: { stage, search: q || undefined } }); setLeads(data); }
    catch { navigate("/admin/login"); }
  }, [stage, q, navigate]);
  useEffect(() => { if (!hasAdminSession()) { navigate("/admin/login"); return; } api.get("/crm/salesmen").then(({ data }) => setSalesmen(data)); api.get("/crm/me").then(({ data }) => setStaff(data)).catch(() => {}); }, [navigate]);
  useEffect(() => { load(); }, [load]);

  const openCreate = () => { setEditId(null); setForm(emptyForm); setCreateOpen(true); };
  const openEdit = (l) => {
    setEditId(l.id);
    setForm({
      company: l.company || "", contact_person: l.contact_person || "", whatsapp: l.whatsapp || "",
      email: l.email || "", city: l.city || "", country: l.country || "", category: l.category || "",
      source: l.source || "", salesman_id: l.salesman_id || "", stage: l.stage || "New",
      frequency_days: l.frequency_days || 7,
    });
    setCreateOpen(true);
  };

  const save = async () => {
    if (!form.company.trim()) { toast.error("Company name required"); return; }
    try {
      if (editId) {
        await api.put(`/crm/leads/${editId}`, form);
        toast.success("Lead updated");
      } else {
        const { data } = await api.post("/crm/leads", form);
        toast.success("Lead created");
        if (data.matched_shop) setMatchPrompt({ lead: data.lead, shop: data.matched_shop });
      }
      setCreateOpen(false); setEditId(null); setForm(emptyForm); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };

  const confirmLinkMatch = async () => {
    if (!matchPrompt) return;
    try {
      await api.post(`/crm/leads/${matchPrompt.lead.id}/convert`, null, { params: { shop_id: matchPrompt.shop.id } });
      toast.success(`Linked to ${matchPrompt.shop.shop_name} and marked Closed Won`);
      setMatchPrompt(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not link"); }
  };

  const doDelete = async () => {
    if (!deleteFor) return;
    try { await api.delete(`/crm/leads/${deleteFor.id}`); toast.success("Lead deleted"); setDeleteFor(null); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not delete lead"); }
  };

  return (
    <AdminShell title="Leads">
      <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
        <div className="flex items-center gap-2 flex-wrap">
          <div className="relative w-full sm:w-64"><Search className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" /><Input data-testid="leads-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search company, contact, city" className="pl-9" /></div>
          <Select value={stage} onValueChange={setStage}><SelectTrigger className="w-40"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="all">All stages</SelectItem>{STAGES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent></Select>
        </div>
        <Button data-testid="lead-new-button" onClick={openCreate} className="gap-2"><Plus className="h-4 w-4" /> New Lead</Button>
      </div>

      <Card className="overflow-hidden"><div className="overflow-x-auto">
        <Table data-testid="leads-table">
          <TableHeader><TableRow className="bg-[#F4EBDD]"><TableHead>Priority</TableHead><TableHead>Lead</TableHead><TableHead>Category</TableHead><TableHead>Salesman</TableHead><TableHead>Stage</TableHead><TableHead>Next Contact</TableHead><TableHead className="text-right">Action</TableHead></TableRow></TableHeader>
          <TableBody>
            {leads.map((l) => (
              <TableRow key={l.id} className={l.followup_bucket === "overdue" ? "bg-[#FCEDED]" : ""}>
                <TableCell><Badge className={BUCKET_BADGE[l.followup_bucket]}>{BUCKET_LABEL[l.followup_bucket]}</Badge></TableCell>
                <TableCell><div className="font-medium">{l.company}</div><div className="text-xs text-muted-foreground">{l.contact_person || "—"} · {l.lead_no}</div>
                  {l.converted_shop_name && <Badge className="mt-1 bg-[#E7F3EA] text-[#1F5B2E] border border-[#BFDCC7]"><Link2 className="h-3 w-3 mr-1" /> {l.converted_shop_name}</Badge>}
                </TableCell>
                <TableCell>{l.category || "—"}</TableCell>
                <TableCell>{l.salesman_name || "—"}</TableCell>
                <TableCell><Badge className={STAGE_BADGE[l.stage] || ""}>{l.stage}</Badge></TableCell>
                <TableCell className="text-sm">{l.next_contact}{l.days_overdue > 0 ? <span className="text-destructive"> · {l.days_overdue}d</span> : null}</TableCell>
                <TableCell className="text-right">
                  <div className="flex items-center justify-end gap-1">
                    <Button data-testid="lead-edit-button" size="sm" variant="ghost" className="gap-1" onClick={() => openEdit(l)}><Pencil className="h-3.5 w-3.5" /> Edit</Button>
                    <Button data-testid="lead-log-button" size="sm" variant="secondary" className="gap-1" onClick={() => { setLogEntity({ type: "lead", id: l.id, name: l.company, ref: l.lead_no }); setLogOpen(true); }}><Phone className="h-3.5 w-3.5" /> Log</Button>
                    {staff?.is_manager && (
                      <Button data-testid="lead-delete-button" size="sm" variant="ghost" className="gap-1 text-destructive hover:text-destructive" onClick={() => setDeleteFor(l)}><Trash2 className="h-3.5 w-3.5" /></Button>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))}
            {leads.length === 0 && <TableRow><TableCell colSpan={7} className="text-center py-10 text-muted-foreground">No leads found.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div></Card>

      <Dialog open={createOpen} onOpenChange={(o) => { setCreateOpen(o); if (!o) { setEditId(null); setForm(emptyForm); } }}>
        <DialogContent data-testid="lead-form" className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display text-2xl">{editId ? "Edit Lead" : "New Lead"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-xs">Company Name *</Label><Input data-testid="lead-company" value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} className="mt-1" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Contact Person</Label><Input value={form.contact_person} onChange={(e) => setForm({ ...form, contact_person: e.target.value })} className="mt-1" /></div>
              <div><Label className="text-xs">WhatsApp</Label><Input value={form.whatsapp} onChange={(e) => setForm({ ...form, whatsapp: e.target.value })} className="mt-1" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">City</Label><Input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} className="mt-1" /></div>
              <div><Label className="text-xs">Country</Label><Input value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} className="mt-1" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Category</Label><Input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="mt-1" /></div>
              <div><Label className="text-xs">Lead Source</Label><Input value={form.source} onChange={(e) => setForm({ ...form, source: e.target.value })} className="mt-1" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Salesman</Label>
                <Select value={form.salesman_id || "__none"} onValueChange={(v) => setForm({ ...form, salesman_id: v === "__none" ? "" : v })}>
                  <SelectTrigger className="mt-1"><SelectValue placeholder="Assign" /></SelectTrigger>
                  <SelectContent><SelectItem value="__none">Unassigned</SelectItem>{salesmen.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label className="text-xs">Frequency</Label>
                <Select value={String(form.frequency_days)} onValueChange={(v) => setForm({ ...form, frequency_days: Number(v) })}><SelectTrigger className="mt-1"><SelectValue /></SelectTrigger><SelectContent>{[3,7,14,30].map((d) => <SelectItem key={d} value={String(d)}>Every {d} days</SelectItem>)}</SelectContent></Select>
              </div>
            </div>
            <div><Label className="text-xs">Stage</Label>
              <Select value={form.stage} onValueChange={(v) => setForm({ ...form, stage: v })}>
                <SelectTrigger data-testid="lead-stage-select" className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>{STAGES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setCreateOpen(false)}>Cancel</Button><Button data-testid="lead-create-submit" onClick={save}>{editId ? "Save Changes" : "Create Lead"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <LogContactDialog open={logOpen} onOpenChange={setLogOpen} entity={logEntity} stages={STAGES} onSaved={load} />

      <Dialog open={!!matchPrompt} onOpenChange={(o) => !o && setMatchPrompt(null)}>
        <DialogContent data-testid="lead-match-dialog">
          <DialogHeader><DialogTitle>Possible Existing Customer</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">This lead’s phone number matches an existing shop: <b className="text-foreground">{matchPrompt?.shop?.shop_name}</b>. Link this lead and mark it as Closed Won?</p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setMatchPrompt(null)}>Not Now</Button>
            <Button data-testid="lead-match-confirm" onClick={confirmLinkMatch}>Link & Convert</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteFor} onOpenChange={(o) => !o && setDeleteFor(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2"><AlertCircle className="h-4 w-4 text-destructive" /> Delete lead — {deleteFor?.company}?</AlertDialogTitle>
            <AlertDialogDescription>This permanently removes the lead and its contact history. This cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep Lead</AlertDialogCancel>
            <AlertDialogAction data-testid="lead-delete-confirm" className="bg-destructive text-destructive-foreground hover:bg-destructive/90" onClick={doDelete}>Delete Lead</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AdminShell>
  );
}
